import PropTypes from 'prop-types';

import React from 'react';

import ReactDOMServer from 'react-dom/server';

import Notification from './notification.jsx';
// Libs
import events from '../../../lib/events.js';

import session from '../../../services/sessionService.jsx';
import {FormattedMessage} from 'react-intl';
import IntlWrapper from 'components/layout/intlWrapper';
import Bowser from 'bowser/es5';

const HTTP_ERROR = 'HTTP_ERROR'; // Use to deduplicate the http error since two spans aren't necessarily equal even if they have the same markup

class NotificationBar extends React.Component {
  static propTypes = {
    debugMode: PropTypes.bool.isRequired,
  };

  state = {
    notifications: [],
    browserErrorDisplayed: false,
  };

  componentDidMount() {
    this._errorCaughtHTTP = events.subscribe('http-error', data => {
      if (data.xhr.status !== 401) {
        let httpError = <FormattedMessage id='general.server.error' />;

        // Dont' add duplicate errors
        if (!this.state.notifications.find(x => x.type === HTTP_ERROR && x.extraClasses === 'error')) {
          this.state.notifications.push({
            message: httpError,
            extraClasses: 'error',
            type: HTTP_ERROR,
          });
        }

        while (this.state.notifications.length > 10) {
          this.state.notifications.shift();
        }
        this.setState({notifications: this.state.notifications});
      }
    });

    this._errorCaught = events.subscribe('error.user', data => {
      let duplicates = this.state.notifications.filter(x => {
        if (data.message.props && x.message.props) {
          return ReactDOMServer.renderToString(<IntlWrapper>{x.message}</IntlWrapper>) === ReactDOMServer.renderToString(<IntlWrapper>{data.message}</IntlWrapper>) && x.extraClasses === 'error';
        }
        return x.message === data.message && x.extraClasses === 'error' || x.key === data.key;
      });

      if(data.userRestricted && !data.userRestricted(session.getUser())) {
        //prevent display error for certain users i.e. scanners errors for wet lab testers
        return;
      }

      if (duplicates.length === 0) {
        this.state.notifications.push({
          message: data.message,
          key: data.key,
          extraClasses: 'error',
        });
        while (this.state.notifications.length > 10) {
          this.state.notifications.shift();
        }
        this.setState({notifications: this.state.notifications});
      }
    });

    this._errorCanceled = events.subscribe('cancel.error', data => {
      this.setState({
        notifications: this.state.notifications.filter(x => !x.key || x.key !== data.key),
      });
    });

    this._warnCaught = events.subscribe('warn.user', data => {
      let extraClasses = 'warning';

      if(data.customClass && data.customClass.trim()) {
        extraClasses = `${extraClasses} ${data.customClass.trim()}`;
      }

      let duplicates = this.state.notifications.filter(x => {
        if (data.message.props && x.message.props) {
          return ReactDOMServer.renderToString(<IntlWrapper>{x.message}</IntlWrapper>) === ReactDOMServer.renderToString(<IntlWrapper>{data.message}</IntlWrapper>) && x.extraClasses === extraClasses;
        }
        return x.message === data.message && x.extraClasses === extraClasses;
      });

      if(data.uniquenessKey) {
        this.state.notifications = this.state.notifications.filter(x => x.uniquenessKey !== data.uniquenessKey);
      }

      if (data.key) {
        this.state.notifications = this.state.notifications.filter(x => x.key !== data.key);
      }

      if (duplicates.length === 0) {
        this.state.notifications.push({
          message: data.message,
          key: data.key,
          extraClasses: extraClasses,
          uniquenessKey: data.uniquenessKey || '',
        });
        while (this.state.notifications.length > 10) {
          this.state.notifications.shift();
        }
        this.setState({notifications: this.state.notifications});
      }
    });

    this._infoCaught = events.subscribe('info.user', data => {
      this.state.notifications.push({
        message: data.message,
      });
      while (this.state.notifications.length > 10) {
        this.state.notifications.shift();
      }
      this.setState({notifications: this.state.notifications});
    });

    this._successCaught = events.subscribe('success.user', data => {
      const notifications = this.state.notifications
        .filter(x => (!x.key || x.key !== data.key) && (!x.key || x.key !== x.clears));

      notifications.push({
        key: data.key,
        message: data.message,
        icon: 'bl-checkmark blue',
        extraClasses: 'success',
      });


      while (notifications.length > 10) {
        notifications.shift();
      }
      this.setState({notifications});
    });

    this._eventPagechange = events.subscribe('page-change', () => {
      const browserError = this.state.notifications.filter(n => n.key === 'browserCompatibility');
      this.setState({notifications: [].concat(browserError)});
      
    });
    
    this.checkBrowserCompatibility(); //before load first page

  }

  componentWillUnmount() {
    this._errorCaughtHTTP.remove();
    this._errorCaught.remove();
    this._warnCaught.remove();
    this._infoCaught.remove();
    this._successCaught.remove();
    this._eventPagechange.remove();
    this._errorCanceled.remove();
  }

  _errorCaught = null;

  _errorCaughtHTTP = null;
  _infoCaught = null;
  _successCaught = null;

  _warnCaught = null;

  closeNotification = (index) => {
    this.state.notifications.splice(index, 1);
    this.setState({notifications: this.state.notifications});
  };

  checkBrowserCompatibility = (setBrowserCompatibilityShown = false) => {
    const browser = Bowser.getParser(window.navigator.userAgent).getBrowser();
    if (browser.name !== 'Chrome') {
      events.publish('error.user', {message: <FormattedMessage id='general.error.browser'/> , key: 'browserCompatibility'});
    }
    if(setBrowserCompatibilityShown) {
      this.setState({browserErrorDisplayed: true});
    }
  };

  render() {
    let notifications = this.state.notifications.map((n, i) => {
      return <Notification key={i} index={i}
                title={n.title} message={n.message} icon={n.icon} extraClasses={n.extraClasses}
                debugMode={true} handleClose={this.closeNotification} />;
    });

    return (
      <div>
        {/* Note this is tied to the nav-bar-height property in the sass files. If you update one you must update the other!*/}
        <div className='notification-bar' style={{top: session.getUser() ? '88px' : 0}}>
          {notifications}
        </div>
        <div style={{height: 54 * this.state.notifications.length, width: '100%'}} />
      </div>
    );
  }
}

export default NotificationBar;
